import { Fragment, useEffect } from 'react';
import { useAutosaveField } from '@/hooks/useAutosave';
import { useRowboat, useSelect } from '@/rowboat';
import type { ItemRow } from '@/schema/estate';
import { plainTagNames } from '@/services/flags';
import { updateEnrichment } from '@/services/itemService';
import type { SuggestionField } from '@/services/suggestionService';
import { removeItemTag } from '@/services/tagService';
import { FieldSuggestions } from './Suggestions';
import { TextArea, TextInput } from './ui';

type EnrichmentKey = 'description' | 'condition_text' | 'provenance' | 'dimensions' | 'materials';

const ENRICHMENT: Array<[EnrichmentKey, string]> = [
  ['description', 'Description'],
  ['condition_text', 'Condition'],
  ['provenance', 'Provenance'],
  ['dimensions', 'Dimensions'],
  ['materials', 'Materials'],
];

const SUGGESTION_FIELD: Partial<Record<EnrichmentKey, SuggestionField>> = {
  description: 'description',
  condition_text: 'condition',
  dimensions: 'dimensions',
  materials: 'materials',
};

type EnrichmentProps = {
  item: ItemRow;
  disabled: boolean;
  /** A Viewer's: hides suggestions and tag removal. */
  readOnly: boolean;
  /** Reports each field's current text, saved or not. Must be stable. */
  onText: (field: string, text: string) => void;
};

export function ItemEnrichment(props: EnrichmentProps) {
  return (
    <section
      aria-label="Details"
      className="space-y-3 rounded-2xl border border-divider bg-surface-elevated p-4 shadow-sm"
    >
      {ENRICHMENT.map(([key, label]) => {
        const suggestionField = SUGGESTION_FIELD[key];
        return (
          <Fragment key={key}>
            <EnrichmentField {...props} field={key} label={label} />
            {suggestionField && (
              <FieldSuggestions
                itemId={props.item.id}
                field={suggestionField}
                readOnly={props.readOnly}
              />
            )}
          </Fragment>
        );
      })}
      <ItemTags item={props.item} disabled={props.disabled} readOnly={props.readOnly} />
      <FieldSuggestions itemId={props.item.id} field="tag" readOnly={props.readOnly} />
    </section>
  );
}

function ItemTags({
  item,
  disabled,
  readOnly,
}: {
  item: ItemRow;
  disabled: boolean;
  readOnly: boolean;
}) {
  const graph = useRowboat();
  const tags = useSelect(
    () => plainTagNames(graph, item.id),
    (a, b) => a.join('\n') === b.join('\n'),
  );
  if (tags.length === 0) return null;
  return (
    <div className="flex items-start gap-2 text-sm text-content-secondary">
      <span className="shrink-0 py-1 font-medium text-content-primary">Tags</span>
      <ul aria-label="Tags" className="flex flex-wrap gap-1.5">
        {tags.map((name) => (
          <li
            key={name}
            className={`flex items-center gap-1 rounded-full border border-divider ${readOnly ? 'px-2 py-1' : 'py-0.5 pr-0.5 pl-2'}`}
          >
            <span>{name}</span>
            {!readOnly && (
              <button
                type="button"
                aria-label={`Remove tag ${name}`}
                disabled={disabled}
                onClick={() =>
                  void removeItemTag(graph, {
                    itemId: item.id,
                    estateId: item.estate_id ?? '',
                    name,
                  })
                }
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-divider bg-surface-primary text-content-secondary hover:bg-interactive-hover disabled:opacity-40"
              >
                ✕
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EnrichmentField({
  item,
  disabled,
  onText,
  field,
  label,
}: EnrichmentProps & { field: EnrichmentKey; label: string }) {
  const graph = useRowboat();
  // draftKey: text typed before a signup identity flip (which remounts the whole tree
  // with a fresh graph) survives in the hook's draft map and rehydrates.
  const value = useAutosaveField(
    item[field] ?? '',
    (v) => updateEnrichment(graph, item.id, { [field]: v }),
    `item:${item.id}:${field}`,
  );
  useEffect(() => onText(field, value.value), [onText, field, value.value]);
  const props = {
    'aria-label': label,
    value: value.value,
    disabled,
    onBlur: () => value.commit(),
    placeholder: label,
  };
  return field === 'description' ? (
    <TextArea {...props} rows={3} onChange={(e) => value.onChange(e.target.value)} />
  ) : (
    <TextInput {...props} onChange={(e) => value.onChange(e.target.value)} />
  );
}
